import * as _ from 'lodash';
import moment from 'moment';
import type { FormEvent, VoidFunctionComponent } from 'react';
import { useEffect, useState } from 'react';
import { Alert, Button, Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';

import Locator from '../../../shared/Locator';
import { useInterval } from '../../hooks';
import BuildStore from '../../redux/builds';
import TaskHelpers from '../../redux/tasks/fns';
import utils from '../../utils';
import AccessGate from '../generic/Gates/AccessGate';
import { RouterLink } from '../generic/Link';
import Loader from '../generic/Loader';
import type { Task } from '../../../shared/model-typings';

type Props = {
  onSubmit?(e: FormEvent<Modal>): void;
  onHide(): void;
  buildId: number;
  show: boolean;
  refId?: string;
};

const getBuildTime = (task: Task): string => {
  switch (task.status) {
    case 'RUNNING':
      return moment.duration(moment().diff(moment(task.started || task.createdAt))).humanize();
    case 'SUCCEEDED':
    case 'FAILED':
      return moment
        .duration(moment(task.started || task.createdAt).diff(task.finished))
        .humanize();
    default:
      return 'N/A';
  }
};

const ModalStreamBuild: VoidFunctionComponent<Props> = (props) => {
  const dispatch = useDispatch();
  useEffect(() => {
    if (props.buildId) {
      dispatch(BuildStore.actions.fetchOne(props.buildId));
    }
  }, [props.buildId, dispatch]);

  const build = useSelector((state) => BuildStore.selectors.getCurrentBuild(state, { id: props.buildId }));
  const buildLocator = build ? new Locator(build.locator) : null;

  const [buildTime, setBuildTime] = useState('');

  useInterval(() => {
    if (build && build.task) {
      setBuildTime(getBuildTime(build.task));
    }
  }, 1000);

  let taskStatus = 'QUEUED';
  if (build?.task) {
    const { task } = build;
    if (task?.status) {
      if (task.status === 'FAILED') {
        if (TaskHelpers.taskHasNoMoreRetries(task)) {
          taskStatus = 'FAILED';
        } else {
          taskStatus = 'RUNNING';
        }
      } else {
        taskStatus = task.status;
      }
    }
  }

  return (
    <AccessGate behavior="showModal" level="free" show={props.show} onHide={props.onHide}>
      <Modal {...props} bsSize="large" backdrop animation onHide={props.onHide}>
        <Modal.Header closeButton>
          <Modal.Title>
            <b>Build Status for:</b>{' '}
            {buildLocator ? (
              <RouterLink to={`/projects/${utils.nuc(buildLocator.toPackageString())}`}>
                {buildLocator.package.value} ({buildLocator.revision.value})
              </RouterLink>
            ) : (
              '...'
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {build ? (
            <>
              {build.provided ? (
                <div className="well well-small text-center">
                  This was a <b>Provided Build</b>, meaning analysis was run in another build environment and uploaded
                  here.
                  <br />
                  Check the logs in your CI or wherever you installed the FOSSA build plugin.
                </div>
              ) : (
                ''
              )}
              <table className="table">
                <thead>
                  <tr>
                    <th>Build ID</th>
                    <th>Status</th>
                    <th>Duration</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <b>#{props.buildId}</b>
                    </td>
                    <td>{taskStatus}</td>
                    <td>{buildTime}</td>
                  </tr>
                </tbody>
              </table>
              {!build.task && <Alert>No task has been assigned to the build yet.</Alert>}
              <div>
                {_.uniq(build.warnings || []).map((warning) => (
                  <div
                    className="alert alert-warning contained-warnings"
                    dangerouslySetInnerHTML={{ __html: warning }}
                  />
                ))}
              </div>
              {build.error && (
                <div className="alert alert-error contained-error" dangerouslySetInnerHTML={{ __html: build.error }} />
              )}
            </>
          ) : (
            <Loader />
          )}
        </Modal.Body>
        <div className="modal-footer">
          <Button onClick={props.onHide}>Close</Button>
        </div>
      </Modal>
    </AccessGate>
  );
};

export default ModalStreamBuild;
